import axiosInstance from '../utils/axios';

/**
 * 감사 로그 용량 모니터링 API 서비스
 * UC-05 감사처리 실패 대응 - 용량 모니터링 시스템
 */
const auditCapacityService = {
  /**
   * 전체 용량 상태 조회
   */
  async getCapacityStatus() {
    try {
      const response = await axiosInstance.get('/audit-monitoring/capacity/status');

      if (response.data.success) {
        // API 응답이 data.data 구조로 중첩되어 있음
        let data = response.data.data?.data || response.data.data || response.data;
        
        // 데이터베이스 용량 정보가 있으면 실제 사용량 계산
        if (data.targets?.database) {
          const db = data.targets.database;
          if (db.total_size_mb > 0) {
            // MB를 바이트로 변환
            data.targets.database.capacity_bytes = db.total_size_mb * 1024 * 1024;
            data.targets.database.used_bytes = db.total_size_mb * 1024 * 1024;
            data.targets.database.usage_percentage = Math.min(100, (db.total_size_mb / 1000) * 100); // 1GB 기준
            
            // 전체 용량 재계산
            data.total_capacity_gb = Math.max(data.total_capacity_gb, db.total_size_mb / 1024);
            data.total_used_gb = db.total_size_mb / 1024;
            data.overall_usage_percentage = data.total_capacity_gb > 0 
              ? (data.total_used_gb / data.total_capacity_gb) * 100 
              : 0;
          }
        }
        
        return { success: true, data };
      }
      return {
        success: false,
        message: response.data.message || '용량 상태를 불러오는데 실패했습니다.',
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        '용량 상태를 불러오는 중 오류가 발생했습니다.';
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 용량 히스토리 조회
   * @param {string} target - 모니터링 대상 (database, file_system, log_files)
   * @param {number} limit - 조회 개수
   * @param {number} page - 페이지 번호
   */
  async getCapacityHistory(target = 'database', limit = 10, page = 1) {
    try {
      const response = await axiosInstance.get('/audit-monitoring/capacity/history', {
        params: { target, limit, page },
      });

      if (response.data.success) {
        // API 응답: { success: true, data: { data: { records: [], total: 0, ... } } }
        const historyData = response.data.data?.data || response.data.data || {};
        const records = historyData.records || [];
        
        return { 
          success: true, 
          data: records,
          total: historyData.total || 0,
          page: historyData.page || page,
          pages: historyData.pages || 0
        };
      }
      return {
        success: false,
        message: response.data.message || '용량 히스토리를 불러오는데 실패했습니다.',
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        '용량 히스토리를 불러오는 중 오류가 발생했습니다.';
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 경보 히스토리 조회
   * @param {number} limit - 조회 개수
   * @param {number} page - 페이지 번호
   */
  async getAlertHistory(limit = 10, page = 1) {
    try {
      const response = await axiosInstance.get('/audit-monitoring/capacity/alerts', {
        params: { limit, page },
      });

      if (response.data.success) {
        // API 응답: { success: true, data: { data: { records: [], total: 0, ... } } }
        const alertData = response.data.data?.data || response.data.data || {};
        const records = alertData.records || [];
        
        return { 
          success: true, 
          data: records,
          total: alertData.total || 0,
          page: alertData.page || page,
          pages: alertData.pages || 0
        };
      }
      return {
        success: false,
        message: response.data.message || '경보 히스토리를 불러오는데 실패했습니다.',
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        '경보 히스토리를 불러오는 중 오류가 발생했습니다.';
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 수동 용량 체크 실행
   */
  async executeManualCheck() {
    try {
      const response = await axiosInstance.post('/audit-monitoring/capacity/check');

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message: response.data.message || '수동 체크 실행에 실패했습니다.',
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        '수동 체크 실행 중 오류가 발생했습니다.';
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 아카이빙 실행
   * @param {number} retentionDays - 보관 기간 (일)
   */
  async executeArchive(retentionDays = 90) {
    try {
      const response = await axiosInstance.post('/audit-monitoring/cleanup/archive', {
        retention_days: retentionDays,
      });

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message: response.data.message || '아카이빙 실행에 실패했습니다.',
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        '아카이빙 실행 중 오류가 발생했습니다.';
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 정리 작업 실행
   * @param {number} retentionDays - 보관 기간 (일)
   */
  async executeCleanup(retentionDays = 90) {
    try {
      const response = await axiosInstance.post('/audit-monitoring/cleanup', {
        retention_days: retentionDays,
      });

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message: response.data.message || '정리 작업 실행에 실패했습니다.',
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        '정리 작업 실행 중 오류가 발생했습니다.';
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 데이터베이스 용량 조회
   */
  async getDatabaseCapacity() {
    try {
      const response = await axiosInstance.get('/audit-monitoring/capacity/database');

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message: response.data.message || '데이터베이스 용량을 불러오는데 실패했습니다.',
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        '데이터베이스 용량을 불러오는 중 오류가 발생했습니다.';
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 파일 시스템 용량 조회
   */
  async getFileSystemCapacity() {
    try {
      const response = await axiosInstance.get('/audit-monitoring/capacity/filesystem');

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message: response.data.message || '파일 시스템 용량을 불러오는데 실패했습니다.',
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        '파일 시스템 용량을 불러오는 중 오류가 발생했습니다.';
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 로그 파일 용량 조회
   */
  async getLogFilesCapacity() {
    try {
      const response = await axiosInstance.get('/audit-monitoring/capacity/logfiles');

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message: response.data.message || '로그 파일 용량을 불러오는데 실패했습니다.',
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        '로그 파일 용량을 불러오는 중 오류가 발생했습니다.';
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 모니터링 설정 조회
   */
  async getSettings() {
    try {
      const response = await axiosInstance.get('/audit-monitoring/settings');

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message: response.data.message || '설정을 불러오는데 실패했습니다.',
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        '설정을 불러오는 중 오류가 발생했습니다.';
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 모니터링 설정 업데이트
   * @param {object} settings - 설정 데이터
   */
  async updateSettings(settings) {
    try {
      const response = await axiosInstance.put('/audit-monitoring/settings', settings);

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message: response.data.message || '설정 저장에 실패했습니다.',
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        '설정 저장 중 오류가 발생했습니다.';
      return { success: false, message: errorMessage };
    }
  },

  /**
   * 대시보드 데이터 조회
   */
  async getDashboardData() {
    try {
      const response = await axiosInstance.get('/audit-monitoring/dashboard');

      if (response.data.success) {
        return { success: true, data: response.data.data };
      }
      return {
        success: false,
        message: response.data.message || '대시보드 데이터를 불러오는데 실패했습니다.',
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        '대시보드 데이터를 불러오는 중 오류가 발생했습니다.';
      return { success: false, message: errorMessage };
    }
  },
};

export default auditCapacityService;
